// free-gift.js  (assets)
// - The drawer shows the freebie visually (cart-free-gift.liquid).
// - "Check out" click: adds / swaps the correct ₹0 gift product so it
//   appears in the Shopify checkout summary.
// - Any cart change (and page load): REMOVES a leftover gift line if the
//   cart no longer qualifies, e.g. all products deleted, so the badge
//   and the empty-cart screen behave like before.

(() => {
  const config = window.freeGiftConfig || {};

  // Highest threshold first; ignore tiers whose product doesn't exist
  const tiers = (config.tiers || [])
    .filter((tier) => tier.variantId)
    .sort((a, b) => b.min - a.min);

  const giftIds = tiers.map((tier) => tier.variantId);
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  let busy = false;
  let queue = Promise.resolve();

  if (!tiers.length) {
    window.FreeGift = { sync() {} };
    console.warn("[FreeGift] No gift products found. Check the handles in free-gift-config.liquid.");
    return;
  }

  /* ---------- helpers ---------- */

  const getCart = () =>
    fetch(`${root}cart.js`, { headers: { Accept: "application/json" } }).then((r) => r.json());

  // All { variantId: qty } changes needed to make the gift correct, or null
  function getUpdates(cart) {
    const current = {};
    let giftTotal = 0;
    giftIds.forEach((id) => (current[id] = 0));

    cart.items.forEach((item) => {
      if (giftIds.includes(item.variant_id)) {
        current[item.variant_id] += item.quantity;
        giftTotal += item.final_line_price;
      }
    });

    // The gift never counts toward its own threshold
    const qualifying = cart.total_price - giftTotal;
    const tier = tiers.find((t) => qualifying >= t.min);
    const wanted = tier ? tier.variantId : null;

    const updates = {};
    giftIds.forEach((id) => {
      const target = id === wanted ? 1 : 0;
      if (current[id] !== target) updates[id] = target;
    });

    return Object.keys(updates).length ? updates : null;
  }

  // Only the removals (target 0); used outside of checkout
  function getRemovals(cart) {
    const updates = getUpdates(cart);
    if (!updates) return null;
    const removals = {};
    Object.keys(updates).forEach((id) => {
      if (updates[id] === 0) removals[id] = 0;
    });
    return Object.keys(removals).length ? removals : null;
  }

  const getDrawer = () => {
    const el = document.querySelector("cart-notification");
    return el && typeof el.getSectionsToRender === "function" ? el : null;
  };

  const getCartPage = () => {
    if (!document.getElementById("main-cart-items")) return null;
    if (!document.getElementById("main-cart-footer")) return null;
    const el = document.querySelector("cart-items");
    return el && typeof el.getSectionsToRender === "function" ? el : null;
  };

  function getSectionIds() {
    const ids = [];
    const drawer = getDrawer();
    const cartPage = getCartPage();
    if (drawer) drawer.getSectionsToRender().forEach((s) => ids.push(s.id));
    if (cartPage) cartPage.getSectionsToRender().forEach((s) => ids.push(s.section));
    return [...new Set(ids)];
  }

  function render(state) {
    const drawer = getDrawer();
    if (drawer) {
      const drawerEl = document.getElementById("mini-cart-drawer");
      const drawerOpen = drawerEl && drawerEl.classList.contains("active");
      drawer.renderContents(state, { skipGiftSync: true, silent: !drawerOpen });
      drawer.currentItemCount = state.item_count;
    }

    const cartPage = getCartPage();
    if (cartPage) {
      cartPage.renderContents(state, true);
      cartPage.currentItemCount = state.item_count;
    }
  }

  async function postUpdates(updates, withSections) {
    const body = { updates };
    if (withSections) {
      body.sections = getSectionIds();
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || "Free gift update failed");
    }
    return data;
  }

  /* ---------- cleanup: remove a gift that no longer qualifies ---------- */

  async function cleanup() {
    const cart = await getCart();
    const removals = getRemovals(cart);
    if (!removals) return;
    const state = await postUpdates(removals, true);
    render(state);
  }

  window.FreeGift = {
    // Called by cart.js / cart-notification.js after every cart change
    sync() {
      queue = queue.then(cleanup).catch((error) => console.error("[FreeGift]", error));
      return queue;
    },
  };

  // Page load: Liquid already knows if a leftover gift needs removing
  if (config.needsCleanup) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => window.FreeGift.sync());
    } else {
      window.FreeGift.sync();
    }
  }

  /* ---------- checkout: add / swap the correct gift ---------- */

  function getCheckoutUrl() {
    let code = "";
    const input = document.getElementById("coupon_code");
    if (input && input.value) code = input.value.trim();

    if (!code) {
      try {
        code = (localStorage.getItem("coupon") || "").trim();
      } catch (e) {}
    }

    if (code === "null") code = "";

    return code
      ? `${root}checkout?discount=${encodeURIComponent(code)}`
      : `${root}checkout`;
  }

  function setLoading(button, on) {
    if (!button) return;
    button.classList.toggle("loading", on);
    if (on) button.setAttribute("aria-disabled", "true");
    else button.removeAttribute("aria-disabled");
  }

  // Capture phase so this runs before the form submits
  document.addEventListener(
    "click",
    async (event) => {
      const button = event.target.closest('[name="checkout"], a[href$="/checkout"]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      if (busy) return;
      busy = true;
      setLoading(button, true);

      try {
        await queue; // let any pending cleanup finish first
        const cart = await getCart();
        const updates = getUpdates(cart);
        if (updates) await postUpdates(updates, false);
      } catch (error) {
        // Never block checkout because of the gift
        console.error("[FreeGift]", error);
      }

      window.location.href = getCheckoutUrl();
    },
    true
  );

  // Reset the button if the customer comes back with the browser Back button
  window.addEventListener("pageshow", () => {
    busy = false;
    document
      .querySelectorAll('[name="checkout"].loading, a[href$="/checkout"].loading')
      .forEach((el) => setLoading(el, false));
  });
})();